import { lazy, ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import CompanyToggle from 'core/widgets/CompanyToggle'
import {
  AccountBankCardUrl,
  AccountDirectDepositUrl,
  AccountFavoritePlacesUrl,
  AccountFavoriteTransactionsUrl,
  AccountFavoriteTripsUrl,
  AccountImporterUrl,
  AccountPremiumUrl,
  AccountProfileUrl,
  AccountPurposesUrl,
  AccountTaxCenterUrl,
  AccountTeamUrl,
  AccountTrackerUrl,
  AccountVehiclesUrl,
  AccountWorkHoursUrl,
  DashboardUrl,
  DirectDepositForm
} from 'constants/RouteUrls'
import NotFoundPage from 'routes/NotFoundPage'
import { lazyLoadHelper } from 'routes/resources'
import Suspense from 'core/components/suspense/Suspense'
import { SkeletonsProps } from 'core/components/loaders/LazyLoadingSkeleton'
import { useSettings } from 'core/hooks/settings/useSettings'

// Lazy-loaded pages
const TrackerPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/tracker/TrackerPage'))
)
const AccountTeamPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/team/AccountTeamPage'))
)
const FavoritePlaces = lazy(() =>
  lazyLoadHelper(() => import('pages/account/favorite-places/FavoritePlaces'))
)
const PurpleLocationsPage = lazy(() =>
  lazyLoadHelper(
    () => import('pages/account/favorite-places/purple/PurpleLocationsPage')
  )
)
const VehiclesPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/vehicles/VehiclesPage'))
)
const ImportPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/import-history/ImportPage'))
)
const TaxCenterPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/tax-center/TaxCenterPage'))
)
const FavoriteTransactionsPage = lazy(() =>
  lazyLoadHelper(
    () => import('pages/account/favorite-transactions/FavoriteTransactionsPage')
  )
)
const Profile = lazy(() =>
  lazyLoadHelper(() => import('pages/account/profile/Profile'))
)
const A2SettingsShell = lazy(() =>
  lazyLoadHelper(() => import('pages/account/settings/A2SettingsShell'))
)
const PurposesPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/purposes/PurposesPage'))
)
const FavoriteTrips = lazy(() =>
  lazyLoadHelper(() => import('pages/account/favorite-trips/FavoriteTrips'))
)
const WorkHours = lazy(() =>
  lazyLoadHelper(() => import('pages/account/work-hours/WorkHours'))
)
const ExpenseSyncPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/expense-sync/ExpenseSyncPage'))
)
const DirectDepositPage = lazy(() =>
  lazyLoadHelper(() => import('pages/account/direct-deposit/DirectDepositPage'))
)
const PremiumPage = lazy(() =>
  lazyLoadHelper(() => import('pages/shared/premium/PremiumPage'))
)

const MotusPageRedirect = lazy(() =>
  lazyLoadHelper(() => import('core/widgets/MotusPageRedirect'))
)

// Route paths are declared absolute in RouteUrls but nested routes are relative
const cleanWildcard = (url: string) => url.replace('/account/', '')

const settingsPage = (
  page: ReactNode,
  variant: SkeletonsProps['variant'] = 'settings'
) => <Suspense variant={variant}>{page}</Suspense>

const RedirectToDashboard = <Navigate to={DashboardUrl} replace />

const AccountRoutes = () => {
  const { data: settings } = useSettings()
  // Strict check: only a boolean `true` opts into the A2 shell (fail-safe).
  const isA2 = settings?.featureFlags?.ENABLE_DRIVER_EXPERIENCE === true

  return (
    <Routes>
      {/* Both experiences */}
      <Route
        path={cleanWildcard(AccountProfileUrl)}
        element={settingsPage(isA2 ? <A2SettingsShell /> : <Profile />)}
      />
      <Route
        path={cleanWildcard(AccountFavoritePlacesUrl)}
        element={settingsPage(
          <>
            <CompanyToggle company="everlance">
              <FavoritePlaces />
            </CompanyToggle>
            <CompanyToggle company="motus">
              <PurpleLocationsPage />
            </CompanyToggle>
          </>
        )}
      />
      <Route
        path={cleanWildcard(AccountDirectDepositUrl)}
        element={settingsPage(
          <MotusPageRedirect
            line1="A separated window will open for you to submit"
            line2="your direct deposit information"
            path={DirectDepositForm}
            buttonText="Go to Compliance"
          >
            <DirectDepositPage />
          </MotusPageRedirect>
        )}
      />

      {/* A2 redirects these to the dashboard */}
      <Route
        path={cleanWildcard(AccountTrackerUrl)}
        element={isA2 ? RedirectToDashboard : settingsPage(<TrackerPage />)}
      />
      <Route
        path={cleanWildcard(AccountTaxCenterUrl)}
        element={isA2 ? RedirectToDashboard : settingsPage(<TaxCenterPage />)}
      />
      <Route
        path={cleanWildcard(AccountTeamUrl)}
        element={isA2 ? RedirectToDashboard : settingsPage(<AccountTeamPage />)}
      />

      {/* Non-A2 only (A2 falls through to NotFound) */}
      {!isA2 && (
        <>
          <Route
            path={cleanWildcard(AccountPurposesUrl)}
            element={settingsPage(<PurposesPage />)}
          />
          <Route
            path={cleanWildcard(AccountFavoriteTripsUrl)}
            element={settingsPage(<FavoriteTrips />)}
          />
          <Route
            path={cleanWildcard(AccountWorkHoursUrl)}
            element={settingsPage(<WorkHours />)}
          />
          <Route
            path={cleanWildcard(AccountVehiclesUrl)}
            element={settingsPage(<VehiclesPage />)}
          />
          <Route
            path={cleanWildcard(AccountBankCardUrl)}
            element={settingsPage(<ExpenseSyncPage />)}
          />
          <Route
            path={cleanWildcard(AccountFavoriteTransactionsUrl)}
            element={settingsPage(<FavoriteTransactionsPage />)}
          />
          <Route
            path={cleanWildcard(AccountImporterUrl)}
            element={settingsPage(<ImportPage />)}
          />
          {/* Shows the proper billing information, cards, invoices etc */}
          <Route
            path={cleanWildcard(AccountPremiumUrl)}
            element={settingsPage(<PremiumPage />, 'billing')}
          />
        </>
      )}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default AccountRoutes
